import { Alert, AlertProps, Button, ConfigProvider, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CloseOutlined } from '@ant-design/icons';

import { BannerType } from 'enums/banner-type.enum';
import { Namespace } from 'enums/namespace.enum';
import { Path } from 'enums/path.enum';

import styles from './AlertBanner.module.scss';
import { useFetchNewsQuery } from '../../services';
import { Banner } from '../../types/banners.types';

type AntdAlertType = AlertProps['type'];
type ValidAntdAlertType = Exclude<AntdAlertType, undefined>;

const ALERT_TYPE_MAP: Record<BannerType, ValidAntdAlertType> = {
  [BannerType.Info]: 'info',
  [BannerType.Warning]: 'warning',
  [BannerType.Error]: 'error',
};

export const AlertBanner = () => {
  const { t } = useTranslation(Namespace.News);
  const { data: activeBanner } = useFetchNewsQuery({ isActive: true });

  const [isHidden, setIsHidden] = useState<boolean>(true);
  const navigate = useNavigate();

  const handleClose = (id: string) => {
    localStorage.setItem('readBannerId', `${id}`);
    setIsHidden(true);
  };

  const handleDetail = (id: string) => {
    localStorage.setItem('readBannerId', `${id}`);
    setIsHidden(true);
    navigate(`/${Path.News}#${id}`);
  };

  useEffect(() => {
    if (activeBanner && activeBanner[0]) {
      const { bannerId } = activeBanner[0];

      const readBannerId = localStorage.getItem('readBannerId');

      if (readBannerId === String(bannerId)) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }
    }
  }, [activeBanner]);

  const banner: Banner | undefined = activeBanner ? activeBanner[0] : undefined;

  const isShowDetailsByType = banner?.type === BannerType.Info || banner?.type === BannerType.Warning;

  return !isHidden && banner ? (
    <div className={styles.alertBanner}>
      <ConfigProvider theme={{ token: { colorWarning: '#faad14' } }}>
        <Alert
          message={banner.title}
          description={
            <>
              <Typography.Paragraph>{banner.description}</Typography.Paragraph>
              {banner.detail && isShowDetailsByType && (
                <Button onClick={() => handleDetail(banner.bannerId)} type="link" className={styles.viewDetail}>
                  {t('viewDetailsLink')}
                </Button>
              )}
            </>
          }
          type={ALERT_TYPE_MAP[banner.type]}
          showIcon
          closable
          closeIcon={<CloseOutlined />}
          onClose={() => handleClose(banner.bannerId)}
        />
      </ConfigProvider>
    </div>
  ) : null;
};
